import { DEFAULT_BRAND_ID, type BrandId, type BrandThemeConfig } from "./types";
import { getBrandTheme, isValidBrandTheme, resolveBrandId } from "./registry";

// loader.ts — the one way the app obtains its brand.
//
// The interface is ASYNCHRONOUS even though today's answer is a bundled object
// resolved in a microtask. That is the whole point: the follow-up ticket that
// moves theme data behind a backend endpoint swaps the source below and every
// consumer — provider, hooks, components — carries on unchanged.

export type BrandThemeSource = (id: BrandId) => Promise<BrandThemeConfig>;

/** Today's source: the bundled local registry. */
export const localBrandThemeSource: BrandThemeSource = async (id) => getBrandTheme(id);

let source: BrandThemeSource = localBrandThemeSource;

/** Point the loader at a different source (the future config API; tests). */
export function setBrandThemeSource(next: BrandThemeSource): void {
  source = next;
}

export function resetBrandThemeSource(): void {
  source = localBrandThemeSource;
}

/** The brand this build was configured for. */
export function configuredBrandId(): BrandId {
  return resolveBrandId(import.meta.env.VITE_BRAND);
}

/**
 * Resolve a brand theme, degrading to SmartMarket rather than failing: an
 * unknown id, a source that rejects, and a config that doesn't satisfy the
 * contract all end up on the default brand.
 */
export async function loadBrandTheme(raw: unknown = import.meta.env.VITE_BRAND): Promise<BrandThemeConfig> {
  const fallback = getBrandTheme(DEFAULT_BRAND_ID);
  const id = resolveBrandId(raw);
  try {
    const config = await source(id);
    if (!isValidBrandTheme(config)) {
      console.warn(`[brand] config for "${id}" failed validation — falling back to ${DEFAULT_BRAND_ID}`);
      return fallback;
    }
    return config;
  } catch (err) {
    console.warn(`[brand] could not load "${id}" — falling back to ${DEFAULT_BRAND_ID}`, err);
    return fallback;
  }
}
